import { useState } from "react";
import { api } from "../lib/api";
import { Modal } from "./Modal";
import type { FormTemplate } from "../lib/types";

/**
 * Fill one form template about one participant — shared by the admin
 * participant profile, the partner client view and anywhere else a form
 * starts from a person rather than from the Forms page. The participant is
 * fixed by the caller; RLS stamps the submitter server-side.
 */
export const FormFillDialog = ({ template, participantId, participantName, onClose, onDone }: {
    template: FormTemplate;
    participantId: string;
    participantName: string;
    onClose: () => void;
    onDone: () => void;
}) => {
    const [answers, setAnswers] = useState<Record<string, string>>({});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const submit = async () => {
        const missing = template.fields.filter((f) => f.required && !answers[f.label]?.trim());
        if (missing.length) {
            setError(`Required: ${missing.map((f) => f.label).join(", ")}`);
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const first = Object.values(answers).find((v) => v.trim());
            await api.post("/form-submissions", {
                template_id: template.id,
                participant_id: participantId,
                summary: (first ?? template.name).slice(0, 160),
                answers,
            });
            onDone();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not submit the form");
            setSaving(false);
        }
    };

    return (
        <Modal
            title={`${template.code} — ${template.name}`}
            subtitle={`About ${participantName}. Answers are snapshotted permanently on submission.`}
            onClose={onClose}
            footer={
                <>
                    <button className="btn" disabled={saving} onClick={() => void submit()}>
                        {saving ? "Submitting…" : "Submit form"}
                    </button>
                    <button className="btn ghost" onClick={onClose} disabled={saving}>Cancel</button>
                </>
            }
        >
            {error && <div className="error-banner">{error}</div>}
            {template.fields.map((f) => (
                <div className="field" key={f.label}>
                    <label htmlFor={`ff-${f.label}`}>{f.label}{f.required ? " *" : ""}</label>
                    {f.kind === "textarea" ? (
                        <textarea id={`ff-${f.label}`}
                            value={answers[f.label] ?? ""}
                            onChange={(e) => setAnswers({ ...answers, [f.label]: e.target.value })} />
                    ) : (
                        <input id={`ff-${f.label}`}
                            value={answers[f.label] ?? ""}
                            onChange={(e) => setAnswers({ ...answers, [f.label]: e.target.value })} />
                    )}
                </div>
            ))}
        </Modal>
    );
};
